import { PHARMACY_NPIS } from '../pharmacies';
import type { MedicationProduct, OnHoldConfig } from '../shared';

const buildOnHoldConfig = (description: string): OnHoldConfig => ({
    allowHold: true,
    pathways: ['self-pay'],
    screens: ['pharmacy-preview'],
    buttonText: 'See other options',
    modal: {
        title: 'Other options',
        body: [
            'If you were expecting a different medicine or form of this medicine, there may be other options available. Consult with your provider to discuss these options.',
            "If you choose 'Confirm and continue' we will put your prescription on hold until we receive a response from your provider or you send it to a pharmacy using the LillyDirect medicine cabinet.",
        ],
        buttonLabels: {
            close: 'Close',
            confirm: 'Confirm and continue',
        },
    },
    holdScreen: {
        alert: {
            color: 'warning',
            body: [
                {
                    key: 'on-hold',
                    text: `<body1>Your current ${description} prescription is on hold</body1>`,
                },
            ],
        },
        body: {
            default: [
                {
                    key: 'default-1',
                    text: '<body1>You recently placed your Mounjaro prescription(s) on hold so you could speak with your provider. All Mounjaro prescriptions of this form in your treatment plan have been temporarily locked while the hold is in place.</body1>',
                },
                {
                    key: 'default-2',
                    text: "<body1>If you're ready to move forward, you can unlock the prescription(s) by confirming below. Once unlocked, the hold will be removed and the prescription(s) will be processed as-is.</body1>",
                },
            ],
        },
        buttonLabels: {
            home: 'Return to home',
            unhold: 'Continue with current prescription',
        },
    },
});

export const mounjaroProducts: MedicationProduct[] = [
    {
        description: 'Mounjaro® Autoinjector 2.5mg/0.5mL',
        dosageForm: 'SOAJ',
        formLabel: 'Autoinjector',
        packageSizeUnit: 'EA',
        strengthUnit: 'mg',
        strengthValue: 2.5,
        imageUrl: '/static/img/mounjaro-2.5mg.png',
        imageOrientation: 'landscape',
        ndcs: [
            { ndc: '00002150680', packageQuantity: 1 },
            { ndc: '00002150601', packageQuantity: 1 },
            { ndc: '00002150661', packageQuantity: 1 },
        ],
        pricing: { selfPay: 499 },
        headlinePrices: [
            {
                type: 'DiscountedCashPrice',
                value: 499,
                pharmacyNpis: [PHARMACY_NPIS.GIFTHEALTH],
            },
        ],
        pathway: {
            initialPathway: 'self-pay',
            initialScreen: 'path-select',
        },
        onHoldConfig: buildOnHoldConfig('Mounjaro® Autoinjector 2.5mg/0.5mL'),
    },
    {
        description: 'Mounjaro® Autoinjector 5mg/0.5mL',
        dosageForm: 'SOAJ',
        formLabel: 'Autoinjector',
        packageSizeUnit: 'EA',
        strengthUnit: 'mg',
        strengthValue: 5,
        imageUrl: '/static/img/mounjaro-5mg.png',
        imageOrientation: 'landscape',
        ndcs: [
            { ndc: '00002149580', packageQuantity: 1 },
            { ndc: '00002149501', packageQuantity: 1 },
        ],
        pricing: { selfPay: 499 },
        headlinePrices: [
            {
                type: 'DiscountedCashPrice',
                value: 499,
                pharmacyNpis: [PHARMACY_NPIS.GIFTHEALTH],
            },
        ],
        pathway: {
            initialPathway: 'self-pay',
            initialScreen: 'path-select',
        },
        onHoldConfig: buildOnHoldConfig('Mounjaro® Autoinjector 5mg/0.5mL'),
    },
    {
        description: 'Mounjaro® Autoinjector 7.5mg/0.5mL',
        dosageForm: 'SOAJ',
        formLabel: 'Autoinjector',
        packageSizeUnit: 'EA',
        strengthUnit: 'mg',
        strengthValue: 7.5,
        imageUrl: '/static/img/mounjaro-7.5mg.png',
        imageOrientation: 'landscape',
        ndcs: [
            { ndc: '00002148480', packageQuantity: 1 },
            { ndc: '00002148401', packageQuantity: 1 },
        ],
        pricing: { selfPay: 499 },
        headlinePrices: [
            {
                type: 'DiscountedCashPrice',
                value: 499,
                pharmacyNpis: [PHARMACY_NPIS.GIFTHEALTH],
            },
        ],
        pathway: {
            initialPathway: 'self-pay',
            initialScreen: 'path-select',
        },
        onHoldConfig: buildOnHoldConfig('Mounjaro® Autoinjector 7.5mg/0.5mL'),
    },
    {
        description: 'Mounjaro® Autoinjector 10mg/0.5mL',
        dosageForm: 'SOAJ',
        formLabel: 'Autoinjector',
        packageSizeUnit: 'EA',
        strengthUnit: 'mg',
        strengthValue: 10,
        imageUrl: '/static/img/mounjaro-10mg.png',
        imageOrientation: 'landscape',
        ndcs: [
            { ndc: '00002147180', packageQuantity: 1 },
            { ndc: '00002147101', packageQuantity: 1 },
        ],
        pricing: { selfPay: 499 },
        headlinePrices: [
            {
                type: 'DiscountedCashPrice',
                value: 499,
                pharmacyNpis: [PHARMACY_NPIS.GIFTHEALTH],
            },
        ],
        pathway: {
            initialPathway: 'self-pay',
            initialScreen: 'path-select',
        },
        onHoldConfig: buildOnHoldConfig('Mounjaro® Autoinjector 10mg/0.5mL'),
    },
    {
        description: 'Mounjaro® Autoinjector 12.5mg/0.5mL',
        dosageForm: 'SOAJ',
        formLabel: 'Autoinjector',
        packageSizeUnit: 'EA',
        strengthUnit: 'mg',
        strengthValue: 12.5,
        imageUrl: '/static/img/mounjaro-12.5mg.png',
        imageOrientation: 'landscape',
        ndcs: [
            { ndc: '00002146080', packageQuantity: 1 },
            { ndc: '00002146001', packageQuantity: 1 },
        ],
        pricing: { selfPay: 499 },
        headlinePrices: [
            {
                type: 'DiscountedCashPrice',
                value: 499,
                pharmacyNpis: [PHARMACY_NPIS.GIFTHEALTH],
            },
        ],
        pathway: {
            initialPathway: 'self-pay',
            initialScreen: 'path-select',
        },
        onHoldConfig: buildOnHoldConfig('Mounjaro® Autoinjector 12.5mg/0.5mL'),
    },
    {
        description: 'Mounjaro® Autoinjector 15mg/0.5mL',
        dosageForm: 'SOAJ',
        formLabel: 'Autoinjector',
        packageSizeUnit: 'EA',
        strengthUnit: 'mg',
        strengthValue: 15,
        imageUrl: '/static/img/mounjaro-15mg.png',
        imageOrientation: 'landscape',
        ndcs: [
            { ndc: '00002145780', packageQuantity: 1 },
            { ndc: '00002145701', packageQuantity: 1 },
        ],
        pricing: { selfPay: 499 },
        headlinePrices: [
            {
                type: 'DiscountedCashPrice',
                value: 499,
                pharmacyNpis: [PHARMACY_NPIS.GIFTHEALTH],
            },
        ],
        pathway: {
            initialPathway: 'self-pay',
            initialScreen: 'path-select',
        },
        onHoldConfig: buildOnHoldConfig('Mounjaro® Autoinjector 15mg/0.5mL'),
    },
];
